import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLocale } from "@/context/LocaleContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { WeeklyIdeasOptIn, useIdeaMailOptIn } from "@/components/WeeklyIdeasOptIn";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Check, Coins, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { trackEvent, planItem } from "@/components/Analytics";

export const PLAN_CATALOG = {
  gratis: {
    id: "gratis", name: "Gratis", tagline: "Til at komme i gang", price: "0 kr.", period: "", credits: "10 kreditter (svarende til ca. 10 opslag om måneden)",
    features: ["Dashboard", "Idégenerator", "LinkedIn-opslag", "Facebook-opslag", "Ugens forslag", "1 bruger - 1 virksomhed"],
  },
  start: {
    id: "start", name: "Start", tagline: "Til solo & mindre virksomheder", price: "395 kr.", period: "/md.", credits: "Udvidet brug (1000 kreditter)",
    features: ["Dashboard", "Idégenerator", "LinkedIn-opslag", "Facebook-opslag", "Debatindlæg", "Nyhedsbrev", "Presse", "Vidensbank", "1 bruger - 1 virksomhed"],
  },
  pro: {
    id: "pro", name: "Pro", tagline: "Den fulde kommunikationsrådgiver", price: "995 kr.", period: "/md.", featured: true, credits: "Ubegrænset brug",
    features: ["Dashboard", "Idégenerator", "LinkedIn-opslag", "Facebook-opslag", "Debatindlæg", "Nyhedsbrev", "Presse", "Kampagner", "Leadgenerator", "Brandfortælling", "Vidensbank", "1 bruger - 1 virksomhed"],
  },
  business: {
    id: "business", name: "Business", tagline: "Til bureauer & teams", price: "2.495 kr.", period: "/md.", credits: "Ubegrænset brug",
    features: ["Dashboard", "Idégenerator", "LinkedIn-opslag", "Facebook-opslag", "Debatindlæg", "Nyhedsbrev", "Presse", "Kampagner", "Leadgenerator", "Brandfortælling", "Vidensbank", "5 brugere - 1 virksomhed"],
  },
};

const TEAL = "#1dddbf";

function Stepper() {
  return (
    <div className="flex items-center justify-center gap-2 mb-10" data-testid="onboarding-stepper">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-full flex items-center justify-center" style={{ backgroundColor: TEAL }}>
            <Check className="h-4 w-4 text-background" strokeWidth={3} />
          </div>
          {i < 3 && <div className="h-0.5 w-10 sm:w-16" style={{ backgroundColor: TEAL }} />}
        </div>
      ))}
    </div>
  );
}

export function PlanSelection({ planIds, mode = "auth", trial = true, showSeeAll = false, seeAllTo = "/planer", showStepper = true, highlightLastWord = false, title = "Vælg dit abonnement", subtitle = "Vælg den løsning, der passer bedst til jeres virksomhed" }) {
  const { user, refresh } = useAuth();
  const ideaMail = useIdeaMailOptIn();
  const { t: _t } = useLocale();
  // Public marketing site is Danish-only; force Danish there. Internal app stays bilingual.
  const t = (s, params) => {
    if (mode === "public") {
      let out = s || "";
      if (params) Object.keys(params).forEach((k) => { out = out.replace(`{${k}}`, params[k]); });
      return out;
    }
    return _t(s, params);
  };
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeDelivery, setAgreeDelivery] = useState(false);

  const hasActiveSub = !!user?.stripe_subscription_id && ["active", "trialing", "past_due"].includes(user?.subscription_status);
  const isPaidUser = mode === "auth" && (user?.plan || "gratis") !== "gratis";
  const isChange = hasActiveSub || isPaidUser;
  // Trial is offered on the public site always (per `trial` prop); inside the app only to free-plan users.
  const showTrial = mode === "public" ? trial : (trial && !hasActiveSub && (user?.plan || "gratis") === "gratis" && !user?.trial_used);
  const plans = planIds.map((id) => PLAN_CATALOG[id]).filter(Boolean);

  const openCheckout = (plan) => {
    trackEvent("select_item", { item_list_name: mode === "public" ? "pricing_public" : "pricing_app", currency: "DKK", items: [planItem(plan.id)] });
    setAgreeTerms(false); setAgreeDelivery(false); setSelectedPlan(plan);
  };

  const trackedList = useRef(false);
  useEffect(() => {
    if (trackedList.current) return;
    trackedList.current = true;
    trackEvent("view_item_list", { item_list_name: mode === "public" ? "pricing_public" : "pricing_app", currency: "DKK", items: planIds.map(planItem) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkout = async () => {
    if (!selectedPlan) return;
    await ideaMail.commit();
    const planId = selectedPlan.id;
    setLoading(planId);
    trackEvent("begin_checkout", { currency: "DKK", value: planItem(planId).price, items: [planItem(planId)] });
    try {
      const { data } = await api.post("/billing/checkout", { plan: planId, origin_url: window.location.origin, trial: showTrial });
      if (data.changed) {
        await refresh();
        setSelectedPlan(null); setLoading(null);
        toast.success(t("Din plan er ændret til {plan}. Betalingen justeres automatisk forholdsmæssigt.", { plan: t(selectedPlan.name) }));
        navigate("/indstillinger");
        return;
      }
      window.location.href = data.url;
    } catch (err) { toast.error(apiError(err)); setLoading(null); }
  };

  const cols = plans.length >= 4 ? "lg:grid-cols-4" : plans.length === 3 ? "lg:grid-cols-3" : "md:grid-cols-2 max-w-4xl";

  return (
    <div>
      {showStepper && <Stepper />}
      <div className="mb-8">
        <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tighter">
          {highlightLastWord ? (() => {
            const parts = t(title).split(" ");
            const last = parts.pop();
            return <>{parts.join(" ")} <span style={{ color: "#1dddbf" }}>{last}</span></>;
          })() : t(title)}
        </h1>
        <p className="text-muted-foreground mt-3">{t(subtitle)}</p>
      </div>

      <div className={`grid grid-cols-1 ${cols} gap-4`}>
        {plans.map((p, i) => {
          const isFree = p.id === "gratis";
          const current = user?.plan === p.id;
          return (
            <motion.div key={p.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
              className={`rounded-2xl border p-6 flex flex-col relative bg-card ${p.featured ? "border-2" : "border-border"}`}
              style={p.featured ? { borderColor: TEAL } : undefined}
              data-testid={`select-plan-${p.id}`}>
              {p.featured && (
                <span className="absolute -top-3 left-6 text-xs font-medium px-3 py-1 rounded-full text-background" style={{ backgroundColor: TEAL }}>
                  {t("Anbefalet")}
                </span>
              )}
              <p className="font-display text-2xl font-medium">{t(p.name)}</p>
              <p className="text-sm text-muted-foreground mt-1">{t(p.tagline)}</p>

              <div className="mt-4 mb-5">
                {isFree ? (
                  <>
                    <span className="font-display text-4xl font-medium tracking-tight">{p.price}</span>
                    <p className="text-sm text-muted-foreground mt-1">{t("Ingen tidsbegrænsning")}</p>
                  </>
                ) : showTrial ? (
                  <>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-muted-foreground line-through text-lg">{p.price}</span>
                      <span className="font-display text-4xl font-medium tracking-tight">0 kr.</span>
                      <span className="rounded-full text-xs font-medium px-2.5 py-1" style={{ backgroundColor: `${TEAL}26`, color: TEAL }}>
                        {t("30 dages gratis")}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">{t("Derefter {price}/md. inkl. moms", { price: p.price })}</p>
                  </>
                ) : (
                  <>
                    <span className="font-display text-4xl font-medium tracking-tight">{p.price}</span>
                    <span className="text-muted-foreground text-sm">{p.period} {t("inkl. moms")}</span>
                  </>
                )}
              </div>

              <div className="mb-5 rounded-lg border border-border bg-secondary/60 px-3 py-2.5 flex items-start gap-2">
                <Coins className="h-4 w-4 text-foreground shrink-0 mt-0.5" />
                {(() => {
                  const m = String(p.credits).match(/^(.*?)\s*\((.*)\)\s*$/);
                  if (!m) return <span className="text-sm font-medium">{t(p.credits)}</span>;
                  return (
                    <span className="text-sm font-medium">
                      {t(m[1].trim())}
                      <span className="block italic font-normal text-muted-foreground">{t(m[2].trim())}</span>
                    </span>
                  );
                })()}
              </div>

              <ul className="space-y-2.5 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="text-sm text-muted-foreground flex gap-2">
                    <Check className="h-4 w-4 shrink-0 mt-0.5" style={{ color: TEAL }} /> {t(f)}
                  </li>
                ))}
              </ul>

              {isFree ? (
                mode === "public" ? (
                  <Button asChild variant="outline" className="mt-6 h-11" data-testid={`select-cta-${p.id}`}>
                    <Link to="/kom-i-gang">{t("Kom i gang gratis")}</Link>
                  </Button>
                ) : (
                  <Button variant="outline" className="mt-6 h-11" onClick={() => navigate("/dashboard")} data-testid={`select-cta-${p.id}`}>
                    {t("Fortsæt")}
                  </Button>
                )
              ) : mode === "public" ? (
                <Button asChild className="mt-6 h-11 font-medium" style={{ backgroundColor: TEAL, color: "#04211c" }} data-testid={`select-cta-${p.id}`}>
                  <Link to={`/kom-i-gang?plan=${p.id}`} onClick={() => trackEvent("select_item", { item_list_name: "pricing_public", currency: "DKK", items: [planItem(p.id)] })}>
                    {showTrial ? t("Prøv gratis i 30 dage") : t("Køb {plan}", { plan: t(p.name) })}
                  </Link>
                </Button>
              ) : current ? (
                <Button className="mt-6 h-11" disabled data-testid={`select-cta-${p.id}`}>{t("Nuværende plan")}</Button>
              ) : (
                <Button className="mt-6 h-11 font-medium" onClick={() => openCheckout(p)} disabled={loading === p.id}
                  style={{ backgroundColor: TEAL, color: "#04211c" }} data-testid={`select-cta-${p.id}`}>
                  {loading === p.id && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  {isChange ? t("Skift til {plan}", { plan: t(p.name) }) : (showTrial ? t("Prøv gratis i 30 dage") : t("Køb {plan}", { plan: t(p.name) }))}
                </Button>
              )}
            </motion.div>
          );
        })}
      </div>

      {showSeeAll && (
        <div className="mt-8 max-w-4xl">
          <Link to={seeAllTo} className="text-sm text-foreground underline underline-offset-4 hover:no-underline" data-testid="see-all-plans-link">
            {t("Se alle planer og funktioner")}
          </Link>
        </div>
      )}

      <Dialog open={!!selectedPlan} onOpenChange={(o) => !o && setSelectedPlan(null)}>
        <DialogContent data-testid="checkout-consent-dialog">
          <DialogHeader>
            <DialogTitle>{isChange ? t("Skift til {plan}", { plan: t(selectedPlan?.name || "") }) : (showTrial ? t("Start gratis prøve af {plan}", { plan: t(selectedPlan?.name || "") }) : t("Bekræft køb af {plan}", { plan: t(selectedPlan?.name || "") }))}</DialogTitle>
            <DialogDescription>
              {selectedPlan?.price}{selectedPlan?.period} {t("inkl. moms")} · {t(selectedPlan?.credits || "")}.{" "}
              {hasActiveSub
                ? t("Dit nuværende abonnement ændres til denne plan. Der oprettes ikke et nyt abonnement, og betalingen justeres automatisk forholdsmæssigt.")
                : (showTrial
                  ? t("Du starter med 30 dages gratis prøveperiode. Der trækkes ikke betaling nu — først når prøveperioden udløber, hvorefter abonnementet fornyes automatisk, indtil det opsiges. Dit betalingskort indtastes ved tilmelding.")
                  : t("Du køber abonnementet nu, og betalingen trækkes med det samme. Abonnementet fornyes automatisk hver måned, indtil det opsiges."))}
            </DialogDescription>
          </DialogHeader>

          {!hasActiveSub && (
            <div className="space-y-4 py-2">
              <label className="flex gap-3 items-start cursor-pointer" data-testid="consent-terms">
                <Checkbox checked={agreeTerms} onCheckedChange={(v) => setAgreeTerms(v === true)} className="mt-0.5" data-testid="consent-terms-checkbox" />
                <span className="text-sm text-muted-foreground leading-relaxed">
                  {t("Jeg har læst og accepterer")}{" "}
                  <Link to="/handelsbetingelser" target="_blank" className="text-foreground underline underline-offset-4 hover:no-underline">{t("handelsbetingelserne")}</Link>.
                </span>
              </label>
              <label className="flex gap-3 items-start cursor-pointer" data-testid="consent-delivery">
                <Checkbox checked={agreeDelivery} onCheckedChange={(v) => setAgreeDelivery(v === true)} className="mt-0.5" data-testid="consent-delivery-checkbox" />
                <span className="text-sm text-muted-foreground leading-relaxed">
                  {t("Jeg accepterer, at levering af den digitale tjeneste påbegyndes med det samme, og at min eventuelle fortrydelsesret bortfalder i det omfang, dette følger af gældende regler om digitale tjenester.")}
                </span>
              </label>
            </div>
          )}

          {ideaMail.show && <WeeklyIdeasOptIn checked={ideaMail.checked} onChange={ideaMail.setChecked} />}

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedPlan(null)} data-testid="consent-cancel-btn">{t("Annullér")}</Button>
            <Button onClick={checkout} disabled={(!hasActiveSub && (!agreeTerms || !agreeDelivery)) || !!loading} data-testid="consent-confirm-btn">
              {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {hasActiveSub ? t("Bekræft planskift") : (showTrial ? t("Start gratis prøve") : t("Fortsæt til betaling"))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
